import { Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
class Row {
    constructor(offset_x, height, offset_y) {
        this.offset_x = offset_x;
        this.height = height;
        this.offset_y = offset_y;
    }
}
export class Rectangle {
    constructor(width, height) {
        this.width = width;
        this.height = height;
        this.rotate = false;
        this.placement = BABYLON_Vector4.Zero();
    }
}
export class RectangleAtlas {
    constructor(desired_texels_per_unit) {
        this._rectangles = [];
        this._resolution_x = 256;
        this._resolution_y = 256;
        this._padding = 2;
        this._desired_texels_per_unit = desired_texels_per_unit;
    }
    increase_resolution_x() {
        // Double the resolution, alternating between x and y to keep aspect ratios sane.
        // I.e. the resolution will increase as follows: 1x1 -> 2x1 -> 2x2 -> 4x2 -> 4x4 -> 8x4...
        if (this._resolution_x >= 16384) {
            throw new Error('UV-atlasing failed, exceeded maximum texture width.');
        }
        this._resolution_x *= 2;
        this._rectangles.forEach((r) => {
            r.placement.x /= 2;
            r.placement.z /= 2;
        });
    }
    increase_resolution_y() {
        if (this._resolution_y >= 16384) {
            throw new Error('UV-atlasing failed, exceeded maximum texture height.');
        }
        this._resolution_y *= 2;
        this._rectangles.forEach((r) => {
            r.placement.y /= 2;
            r.placement.w /= 2;
        });
    }
    // Add a atlasing job, return ID of job
    add_rectangle(width, height) {
        const w = Math.max(2, Math.ceil(width * this._desired_texels_per_unit));
        const h = Math.max(2, Math.ceil(height * this._desired_texels_per_unit));
        const rectangle = new Rectangle(w, h);
        this._rectangles.push(rectangle);
        return rectangle;
    }
    get_resolution() {
        return {
            width: this._resolution_x,
            height: this._resolution_y
        };
    }
    atlas() {
        // Rectangles with most height first
        this._rectangles.sort(function (a, b) {
            const d = b.height - a.height;
            if (d !== 0)
                return d;
            return b.width - a.width;
        });
        let next_start_y = 0;
        const rows = [];
        this._rectangles.forEach((r) => {
            while (r.width > this._resolution_x) {
                this.increase_resolution_x();
            }
            // Find a row where we can fit. Otherwise create a new row
            let placed_row = -1;
            let best_row = -1;
            let best_row_offset = Infinity;
            const num_rows = rows.length;
            for (let i = 0; i < num_rows; i++) {
                const row = rows[i];
                if (row.height < r.height) {
                    // TODO: We could expand last row but due to sorting this shouldn't happen
                    continue;
                }
                if (row.offset_x + r.width + 2 * this._padding >= this._resolution_x) {
                    // Can't fit in row due to width
                    if (row.offset_x < best_row_offset) {
                        best_row = i;
                        best_row_offset = row.offset_x;
                    }
                    continue;
                }
                // If geometry is low compared to row we could split row heightwise. We know following pieces will be no higher than this one
                placed_row = i;
                break;
            }
            if (placed_row === -1) {
                const new_start_y = next_start_y + r.height + this._padding;
                if (this._resolution_y > this._resolution_x &&
                    new_start_y > this._resolution_y) {
                    while (rows[best_row].offset_x + r.width + this._padding >=
                        this._resolution_x) {
                        this.increase_resolution_x();
                    }
                    placed_row = best_row;
                }
                else {
                    while (new_start_y > this._resolution_y) {
                        this.increase_resolution_y();
                    }
                    const row = new Row(0, r.height, next_start_y);
                    placed_row = rows.length;
                    rows.push(row);
                    next_start_y = new_start_y;
                }
            }
            // Place rectangle into row with index placed_row
            const row = rows[placed_row];
            if (!r.rotate) {
                const p = this._padding;
                const x0 = (row.offset_x + p) / this._resolution_x;
                const y0 = (row.offset_y + p) / this._resolution_y;
                r.placement = new BABYLON_Vector4(x0, y0, x0 + r.width / this._resolution_x, y0 + r.height / this._resolution_y);
            }
            else {
                // TODO: Add support for rotation
            }
            if (r.height < 0.9 * row.height) {
                rows.splice(placed_row + 1, 0, new Row(row.offset_x, row.height - r.height - this._padding, row.offset_y + r.height + this._padding));
                row.height = r.height;
            }
            row.offset_x += r.width + this._padding;
        });
    }
}
//# sourceMappingURL=RectangleAtlas.js.map